import React from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';
import { db } from '../services/firebaseConfig';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { callContarAnuncio } from '../services/cloudFunctions';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ESPACIO PATROCINADO — una marca, en el hueco donde hay atención
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Petición del fundador (3-oct-2026), y su aclaración: «al estilo de como Uber
 * lo muestra, pero no tenía pensado meterle AdMob, solo marcas que deseen
 * tráfico para su negocio».
 *
 * ── DÓNDE, Y POR QUÉ AHÍ ─────────────────────────────────────────────────
 *
 *   · `espera` — mientras el pasajero espera a su chofer. Son de tres a ocho
 *     minutos mirando un mapa sin nada que hacer: el único hueco de la app con
 *     atención de sobra. Va al FINAL del panel, debajo de la tarjeta del
 *     chofer, del PIN y de los botones de pago. Nada de lo que importa se
 *     mueve de sitio por un anuncio.
 *   · `recibo` — al terminar el viaje.
 *
 * ── A QUIÉN **NO** SE LE ENSEÑA ──────────────────────────────────────────
 *
 * Al CHOFER, nunca. Paga una membresía todos los meses precisamente para no
 * ser el producto; meterle publicidad encima tumba el argumento de que aquí
 * ganan los dos. El componente ni siquiera lo intenta: sin rol de pasajero
 * devuelve null antes de leer nada.
 *
 * ── LO QUE SE CUENTA ─────────────────────────────────────────────────────
 *
 * Una vista por persona y día, no por aparición. El contador lo lleva el
 * servidor (`contarAnuncio`); aquí solo se avisa una vez por montaje. Es el
 * número con el que el fundador le pone precio al siguiente cliente, así que
 * tiene que ser el que aguante que se lo discutan.
 *
 * ── SI ALGO FALLA, NO SE VE NADA ─────────────────────────────────────────
 *
 * Todo el componente devuelve null ante cualquier fallo. Un anuncio es lo
 * ÚLTIMO que puede romper la pantalla donde alguien está esperando un carro.
 */

type Anuncio = {
  id: string;
  marca: string;
  titulo: string;
  texto: string;
  imagenUrl: string | null;
  enlace: string | null;
};

const ROLES_CON_ANUNCIOS = ['PASSENGER', 'CAR_RENTER', 'FOOD_DELIVERY'];

export default function EspacioPatrocinado({ hueco }: { hueco: 'espera' | 'recibo' }) {
  const colores = useThemeColors();
  const rol = useAppStore((s) => s.userRole);
  const uid = useAppStore((s) => s.userFirebaseUid);
  // ⚠️ QUIEN PAGA MEMBRESIA NO VE ANUNCIOS, ESTE EN EL MODO QUE ESTE.
  // Mirar solo el rol no basta: la app deja «Cambiar a Pasajero», y un chofer
  // aprobado que se cambia de modo seguiria siendo alguien que paga todos los
  // meses. El principio del fundador es que quien paga no es el producto, y ese
  // principio es de la CUENTA, no del modo en que esta en ese momento.
  const esChoferAprobado = useAppStore((s) => s.isDriverVerified);
  const [a, setA] = React.useState<Anuncio | null>(null);
  const contado = React.useRef(false);

  // Al chofer no se le enseña nada, y ni se consulta.
  const puedeVer =
    !!uid && !esChoferAprobado && ROLES_CON_ANUNCIOS.includes(String(rol ?? ''));

  React.useEffect(() => {
    if (!puedeVer) return;
    let vivo = true;
    (async () => {
      try {
        // Se lee DIRECTO de Firestore, no por una Cloud Function: esto se pinta
        // mientras alguien espera su carro, y el arranque en frío de una
        // función son segundos justo donde no sobran.
        let filas: any[] = [];
        if (Platform.OS !== 'web') {
          const s = await nativeFirestore()
            .collection('anuncios')
            .where('activo', '==', true)
            .limit(20)
            .get();
          filas = (s?.docs ?? []).map((d: any) => ({ id: d.id, ...(d.data() ?? {}) }));
        } else {
          const s = await getDocs(
            query(collection(db, 'anuncios'), where('activo', '==', true), limit(20))
          );
          filas = s.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        }
        if (!vivo) return;

        const ahora = Date.now();
        const validos = filas.filter(
          (x) =>
            Array.isArray(x.donde) &&
            x.donde.includes(hueco) &&
            x.imagenUrl &&
            (!x.desdeMs || ahora >= x.desdeMs) &&
            (!x.hastaMs || ahora <= x.hastaMs)
        );
        if (!validos.length) return;

        // Uno al azar por montaje: con varios anunciantes, todos salen por
        // igual sin que haga falta llevar cuentas de a quién le toca.
        const elegido = validos[Math.floor(Math.random() * validos.length)];
        setA({
          id: elegido.id,
          marca: String(elegido.marca ?? ''),
          titulo: String(elegido.titulo ?? ''),
          texto: String(elegido.texto ?? ''),
          imagenUrl: String(elegido.imagenUrl ?? '') || null,
          enlace: elegido.enlace ? String(elegido.enlace) : null,
        });
      } catch (e: any) {
        // Silencio a propósito: ver la nota de arriba.
        console.warn('[patrocinado] no se pudo leer:', e?.message);
      }
    })();
    return () => { vivo = false; };
  }, [puedeVer, hueco]);

  React.useEffect(() => {
    if (!a || contado.current) return;
    contado.current = true;
    callContarAnuncio({ id: a.id, tipo: 'vista' }).catch(() => undefined);
  }, [a]);

  if (!puedeVer || !a || !a.imagenUrl) return null;

  const abrir = () => {
    if (!a.enlace) return;
    callContarAnuncio({ id: a.id, tipo: 'clic' }).catch(() => undefined);
    Linking.openURL(a.enlace).catch(() => undefined);
  };

  return (
    <TouchableOpacity
      onPress={abrir}
      activeOpacity={a.enlace ? 0.85 : 1}
      disabled={!a.enlace}
      accessibilityRole={a.enlace ? 'link' : 'image'}
      accessibilityLabel={`Publicidad de ${a.marca}: ${a.titulo}`}
      style={[estilos.caja, { backgroundColor: colores.card, borderColor: colores.border }]}
    >
      {/* ⚠️ ESTA ETIQUETA ES OBLIGATORIA, NO DECORATIVA.
          La Ley 358-05 de Protección al Consumidor (RD) exige que la
          publicidad se identifique COMO publicidad. El proyecto ya tomó esa
          decisión una vez —la pastilla «PATROCINADO» de
          `RotatingPromoBanner.tsx`, donde está escrita la misma ley— y aquí se
          mantiene el criterio.
          Y al margen de la ley: que alguien crea que un anuncio es un aviso de
          Go Safely es engañarlo, justo en la pantalla donde está esperando que
          nosotros le mandemos un carro. */}
      <View style={estilos.etiquetaFila}>
        <Text style={[estilos.etiqueta, { color: colores.textSecondary }]}>
          PUBLICIDAD · {a.marca.toUpperCase()}
        </Text>
      </View>

      <Image
        source={{ uri: a.imagenUrl }}
        style={estilos.imagen}
        resizeMode="cover"
        accessible={false}
      />

      <View style={estilos.texto}>
        <Text style={[estilos.titulo, { color: colores.text }]} numberOfLines={2}>
          {a.titulo}
        </Text>
        {a.texto ? (
          <Text style={[estilos.cuerpo, { color: colores.textSecondary }]} numberOfLines={2}>
            {a.texto}
          </Text>
        ) : null}
      </View>

      {a.enlace ? (
        <View style={estilos.pie}>
          <Text style={[estilos.verMas, { color: colores.primary }]}>Ver más</Text>
          <Ionicons name="open-outline" size={13} color={colores.primary} />
        </View>
      ) : null}
    </TouchableOpacity>
  );
}

const estilos = StyleSheet.create({
  caja: { borderRadius: 14, borderWidth: 1, overflow: 'hidden', marginTop: 14 },
  etiquetaFila: { paddingHorizontal: 12, paddingTop: 9, paddingBottom: 6 },
  etiqueta: { fontSize: 10, fontWeight: '800', letterSpacing: 1 },
  imagen: { width: '100%', height: 130, backgroundColor: 'rgba(127,127,127,0.1)' },
  texto: { paddingHorizontal: 12, paddingTop: 10 },
  titulo: { fontSize: 15, fontWeight: '700', lineHeight: 20 },
  cuerpo: { fontSize: 13, lineHeight: 18, marginTop: 3 },
  pie: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10 },
  verMas: { fontSize: 13, fontWeight: '700', marginRight: 4 },
});
